import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Text, Button } from "@chakra-ui/react";
import { LogOut, Settings, Building2 } from "lucide-react";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { purgeDeviceData } from "@/lib/deviceCleanup";
import { outboxCount } from "@/lib/offline";
import { ConfirmModal } from "@/components/ConfirmModal";
import { colors } from "@/theme/colors";

export const UserMenu = () => {
  const { profile, user, role, companyName, isSuperAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unsentWarn, setUnsentWarn] = useState(0); // >0 = confirm modal open with N unsent forms
  const [signingOut, setSigningOut] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const doSignOut = async () => {
    setSigningOut(true);
    await signOut();
    await purgeDeviceData(); // wipe drafts, queued forms and SW caches for the next user
    navigate("/login", { replace: true });
  };

  // Step 11 scenario 8: never silently destroy unsent field work — warn first.
  const handleSignOut = async () => {
    setOpen(false);
    const n = user ? await outboxCount(user.id) : 0;
    if (n > 0) { setUnsentWarn(n); return; }
    await doSignOut();
  };

  const name = (profile?.full_name || "").trim() || user?.email || "User";
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const roleLabel = role ? ROLE_LABELS[role] : "Pending";
  const scope = isSuperAdmin ? "All companies" : companyName || "—";

  return (
    <Box position="relative" ref={ref}>
      <Flex
        as="button"
        align="center"
        justify="center"
        w="38px"
        h="38px"
        borderRadius="full"
        bg={colors.sage}
        color="white"
        fontFamily="heading"
        fontWeight="700"
        fontSize="sm"
        onClick={() => setOpen((v) => !v)}
        data-testid="user-menu-button"
        cursor="pointer"
      >
        {initials}
      </Flex>

      {open && (
        <Box
          position="absolute"
          right="0"
          top="48px"
          w="260px"
          bg={colors.white}
          border="1px solid"
          borderColor={colors.border}
          borderRadius="12px"
          boxShadow="0 8px 24px rgba(31,41,55,0.12)"
          p="4"
          zIndex={50}
          data-testid="user-menu-panel"
        >
          <Text fontWeight="600" color={colors.textPrimary} data-testid="user-menu-name">
            {name}
          </Text>
          <Text fontSize="sm" color={colors.textSecondary} mb="3" wordBreak="break-all">
            {user?.email}
          </Text>
          <Box bg={colors.tealTint} borderRadius="8px" px="3" py="2" mb="1">
            <Text fontSize="xs" color={colors.textSecondary}>Role</Text>
            <Text fontSize="sm" fontWeight="600" color={colors.tealHover} data-testid="user-menu-role">{roleLabel}</Text>
          </Box>
          <Box bg={colors.cream} borderRadius="8px" px="3" py="2" mb="3">
            <Text fontSize="xs" color={colors.textSecondary}>Company</Text>
            <Text fontSize="sm" fontWeight="600" color={colors.textPrimary} data-testid="user-menu-company">{scope}</Text>
          </Box>
          <Button
            w="full"
            size="sm"
            variant="ghost"
            justifyContent="flex-start"
            color={colors.textPrimary}
            onClick={() => { setOpen(false); navigate("/account"); }}
            data-testid="menu-account-settings"
          >
            <Settings size={16} /> Account settings
          </Button>
          {(role === "org_admin" || isSuperAdmin) && (
            <Button
              w="full"
              size="sm"
              variant="ghost"
              justifyContent="flex-start"
              color={colors.textPrimary}
              onClick={() => { setOpen(false); navigate(isSuperAdmin ? "/platform" : "/settings"); }}
              data-testid="menu-company-settings"
            >
              <Building2 size={16} /> Company settings
            </Button>
          )}
          <Box borderTop="1px solid" borderColor={colors.border} my="2" />
          <Button
            w="full"
            size="sm"
            variant="outline"
            onClick={handleSignOut}
            data-testid="signout-button"
          >
            <LogOut size={16} /> Sign out
          </Button>
        </Box>
      )}

      <ConfirmModal
        open={unsentWarn > 0}
        onClose={() => setUnsentWarn(0)}
        title="Unsent forms on this device"
        body={`You have ${unsentWarn} unsent form${unsentWarn === 1 ? "" : "s"} waiting to sync. Signing out permanently deletes ${unsentWarn === 1 ? "it" : "them"} from this device.\n\nSign out anyway?`}
        confirmLabel="Sign out anyway"
        danger
        busy={signingOut}
        onConfirm={doSignOut}
        testId="unsent-signout-modal"
      />
    </Box>
  );
};
